import { useValue } from "@legendapp/state/react";
import { useRouterState } from "@tanstack/react-router";

import { boardPage$ } from "~/pages/board/board.store";
import { datasetPage$ } from "~/pages/dataset/dataset.store";
import { blocks$ } from "~/stores/blocks/blocks.crud";
import { boards$ } from "~/stores/boards/boards.crud";
import { datasets$ } from "~/stores/datasets/datasets.crud";
import { MentionChipDisplay } from "./chat-input-editor";

/**
 * Mirrors `getChatContext()` in the UI: shows one chip per ambient entity the
 * supervisor will receive in `<current_context>` — the active dataset/board
 * and the focused block on a board page. Read-only display only.
 */
export const ChatContextChips = () => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isDatasetPage = pathname.startsWith("/datasets/");
  const isBoardPage = pathname.startsWith("/boards/");

  const datasetId = useValue(datasetPage$.activeDatasetId);
  const boardId = useValue(boardPage$.activeBoardId);
  const focusedBlockId = useValue(boardPage$.selectedBlockId);

  const datasetName = useValue(datasets$[datasetId]?.name);
  const boardName = useValue(boards$[boardId]?.name);
  const focusedBlockName = useValue(blocks$[focusedBlockId ?? ""]?.name);

  const chips: {
    kind: "dataset" | "board" | "block";
    id: string;
    label: string;
  }[] = [];

  if (isDatasetPage && datasetId) {
    chips.push({
      kind: "dataset",
      id: datasetId,
      label: datasetName || "Untitled dataset",
    });
  } else if (isBoardPage && boardId) {
    chips.push({
      kind: "board",
      id: boardId,
      label: boardName || "Untitled board",
    });
    if (focusedBlockId) {
      chips.push({
        kind: "block",
        id: focusedBlockId,
        label: focusedBlockName || "Untitled block",
      });
    }
  }

  if (chips.length === 0) return null;

  return (
    <div className="no-scrollbar flex gap-1 overflow-x-auto *:shrink-0">
      {chips.map((c) => (
        <MentionChipDisplay
          key={`${c.kind}:${c.id}`}
          id={c.id}
          kind={c.kind}
          label={c.label}
        />
      ))}
    </div>
  );
};

/**
 * Non-rendering helper that mirrors `ChatContextChips`'s emptiness check so
 * the parent can avoid mounting an `InputGroupAddon` (and its padding) when
 * there's nothing to show.
 */
export const useHasChatContextChips = (): boolean => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const datasetId = useValue(datasetPage$.activeDatasetId);
  const boardId = useValue(boardPage$.activeBoardId);
  const focusedBlockId = useValue(boardPage$.selectedBlockId);

  if (pathname.startsWith("/datasets/")) return Boolean(datasetId);
  if (pathname.startsWith("/boards/"))
    return Boolean(boardId || focusedBlockId);
  return false;
};
